import React from 'react';

/**
 * ResultRowsEditor
 * ----------------
 * Structured analyte entry for hand-keyed lab results, shared by the in-house lab
 * order workflow (LabResultsWorkflow) and the referral results panel (Labs.js).
 *
 * Replaces the old free-text results box. The rows posted here go through the same
 * PDF builder and the same abnormal evaluation as results that arrive automatically
 * from an analyzer, so a manual report and an automatic one are indistinguishable —
 * and a manually entered out-of-range value now flags the order in the worklist.
 *
 * Column meanings match the analyzer's canonical payload:
 *   name      the analyte as it should read on the report (e.g. 'Sodium')
 *   value     the result, as measured ('140', '6.02', 'neg')
 *   units     e.g. 'mmol/L'
 *   refRange  the clinical reference interval as 'low-high' (e.g. '128-145').
 *             Parsed for abnormal detection, so the low-high form matters.
 *   flag      N normal · H high · L low · A abnormal · HH/LL critical · blank
 *
 * A blank flag is fine: when refRange is a parseable low-high interval the LIS
 * decides from the value. Leave BOTH blank only if the result genuinely cannot be
 * evaluated — that resolves to 'indeterminate', never a silent 'normal'.
 *
 * Props:
 *   rows      array of { name, value, units, refRange, flag }
 *   onChange  (nextRows) => void
 *   disabled  disables all inputs while a submit is in flight
 */

export const EMPTY_ROW = { name: '', value: '', units: '', refRange: '', flag: '' };

// A row counts as filled in once it has both something to call it and a result.
export const isCompleteRow = (r) => Boolean(r && r.name.trim() && r.value.trim());

const FLAGS = [
  { value: '',   label: '—' },
  { value: 'N',  label: 'N — Normal' },
  { value: 'H',  label: 'H — High' },
  { value: 'L',  label: 'L — Low' },
  { value: 'A',  label: 'A — Abnormal' },
  { value: 'HH', label: 'HH — Critical High' },
  { value: 'LL', label: 'LL — Critical Low' },
];

const cellInput = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '6px 8px',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  fontSize: '13px',
};

const th = {
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: 700,
  color: '#374151',
  padding: '0 6px 6px',
};

const ResultRowsEditor = ({ rows, onChange, disabled = false }) => {
  const setCell = (index, field, value) => {
    const next = rows.map((r, i) => (i === index ? { ...r, [field]: value } : r));
    // Keep exactly one trailing blank row so there is always somewhere to type
    // without making the user click "Add row" for every analyte.
    if (index === rows.length - 1 && value !== '') next.push({ ...EMPTY_ROW });
    onChange(next);
  };

  const removeRow = (index) => {
    const next = rows.filter((_, i) => i !== index);
    onChange(next.length ? next : [{ ...EMPTY_ROW }]);
  };

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '620px', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: '30%' }}>Test</th>
              <th style={{ ...th, width: '15%' }}>Result</th>
              <th style={{ ...th, width: '15%' }}>Units</th>
              <th style={{ ...th, width: '20%' }}>Reference Range</th>
              <th style={{ ...th, width: '17%' }}>Flag</th>
              <th style={{ ...th, width: '3%' }} aria-label="Remove row" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td style={{ padding: '3px 6px' }}>
                  <input
                    style={cellInput}
                    value={row.name}
                    disabled={disabled}
                    placeholder="Sodium"
                    onChange={(e) => setCell(i, 'name', e.target.value)}
                  />
                </td>
                <td style={{ padding: '3px 6px' }}>
                  <input
                    style={cellInput}
                    value={row.value}
                    disabled={disabled}
                    placeholder="140"
                    onChange={(e) => setCell(i, 'value', e.target.value)}
                  />
                </td>
                <td style={{ padding: '3px 6px' }}>
                  <input
                    style={cellInput}
                    value={row.units}
                    disabled={disabled}
                    placeholder="mmol/L"
                    onChange={(e) => setCell(i, 'units', e.target.value)}
                  />
                </td>
                <td style={{ padding: '3px 6px' }}>
                  <input
                    style={cellInput}
                    value={row.refRange}
                    disabled={disabled}
                    placeholder="128-145"
                    onChange={(e) => setCell(i, 'refRange', e.target.value)}
                  />
                </td>
                <td style={{ padding: '3px 6px' }}>
                  <select
                    style={cellInput}
                    value={row.flag}
                    disabled={disabled}
                    onChange={(e) => setCell(i, 'flag', e.target.value)}
                  >
                    {FLAGS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      disabled={disabled}
                      title="Remove this row"
                      style={{
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        color: '#9ca3af', fontSize: '16px', lineHeight: 1, padding: '4px',
                      }}
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={() => onChange([...rows, { ...EMPTY_ROW }])}
        disabled={disabled}
        style={{
          marginTop: '8px', padding: '5px 12px', fontSize: '13px',
          border: '1px solid #0e8a8f', borderRadius: '4px',
          background: '#fff', color: '#0e8a8f', cursor: 'pointer', fontWeight: 600,
        }}
      >
        + Add row
      </button>

      <p style={{ fontSize: '12px', color: '#6b7280', margin: '8px 0 0' }}>
        Enter the reference range as <strong>low-high</strong> (e.g. <code>128-145</code>) so
        out-of-range values are detected automatically. Set the flag when the instrument
        reported one; leave it blank to let the range decide.
      </p>
    </div>
  );
};

export default ResultRowsEditor;
